import {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import { Close } from "@mui/icons-material";

import * as Yup from "yup";
import axios from "axios";
import { format } from "date-fns";
import { useFormik } from "formik";
import { useQueryClient } from "react-query";

import {
    useDeleteAsset,
    useGetAdmins,
    useMessage,
    useUpdateAsset,
} from "../../sdk/hooks";
import { IAssetsModel } from "../../sdk/types";

import { ProductVideo } from "./ProductVideo";

const StyledDialog = styled(Dialog)``;

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(3)};
    padding: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
`;

const StyledDialogTitle = styled(DialogTitle)`
    width: 100%;
    background-color: #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(1, 2)};
`;

const StyledDialogActions = styled(DialogActions)`
    padding: ${({ theme }) => theme.spacing(1, 3, 3, 3)};
`;

const VariantImage = styled("img")`
    height: 500px;
    width: auto;
    max-width: 50%;
    border-radius: ${({ theme }) => theme.spacing(1)};
    cursor: pointer;
    object-fit: contain;
`;

const VideoContainer = styled("div")`
    height: 500px;
    width: auto;
    max-width: 50%;
    border-radius: ${({ theme }) => theme.spacing(1)};
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DetailsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    width: 50%;
`;

const DetailRow = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
`;

const DetailLabel = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled(Typography)`
    font-size: 14px;
`;

const DeleteButton = styled(Button)`
    width: 200px;
    height: 40px;
`;

const SaveButton = styled(Button)`
    width: 200px;
    height: 40px;
`;

const tagOptions = ["primary", "wsyb", "care"];

export interface AssetDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    assetDetails?: IAssetsModel;
    url: string;
    assetId: string | null;
}

const validationSchema = Yup.object().shape({
    position: Yup.number().min(1, "Position must be at least 1").required(),
    header: Yup.string(),
    description: Yup.string(),
    altText: Yup.string(),
    tags: Yup.string(),
});

export const AssetDetailsDialog: FunctionComponent<AssetDetailsDialogProps> = (
    props: AssetDetailsDialogProps,
): ReactElement => {
    const { open, onClose, assetDetails, url, assetId } = props;

    const [dimensions, setDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);

    const deleteAsset = useDeleteAsset(["products-delete"]);
    const updateAsset = useUpdateAsset(["products-update"]);
    const queryClient = useQueryClient();

    const [fileSize, setFileSize] = useState<number | null>(null);

    const { data: admins } = useGetAdmins("admins");

    const admin = admins?.records.find((adminRecord) => {
        return adminRecord.id === assetDetails?.uploadedBy;
    });

    const { showError, showSuccess } = useMessage();

    const formik = useFormik({
        initialValues: {
            position: assetDetails?.productImage?.position || 1,
            header: assetDetails?.productImage?.header || "",
            description: assetDetails?.productImage?.description || "",
            altText: assetDetails?.productImage?.altText || "",
            tags: assetDetails?.tags || "",
        },
        validationSchema,
        onSubmit: (values) => {
            if (assetId) {
                updateAsset.mutate(
                    { assetId, parameters: values },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries([
                                "assets",
                                "products",
                            ]);
                            showSuccess("Asset updated successfully");
                            onClose();
                        },
                    },
                );
            }
        },
        enableReinitialize: true,
    });

    const checkVideo = useCallback((fileUrl: string) => {
        if (!fileUrl) {
            return false;
        }
        return /\.(mp4|mov|webm|avi|mkv)$/i.test(fileUrl);
    }, []);

    const handleDelete = useCallback(() => {
        if (assetId) {
            deleteAsset.mutate(assetId, {
                onSuccess: () => {
                    onClose();
                    showError("Asset deleted successfully");
                },
            });
        }
    }, [assetId, deleteAsset, onClose, showError]);

    useEffect(() => {
        const fetchFileInformation = async () => {
            if (!url || url.trim().length === 0) {
                return;
            }
            const videoFile = checkVideo(url);
            try {
                if (videoFile) {
                    setDimensions({
                        width: 1920,
                        height: 1080,
                    });
                    const response = await axios.get(url, {
                        responseType: "blob",
                    });
                    setFileSize(response.data.size / 1024);
                } else {
                    const image = new Image();
                    image.src = url;
                    await new Promise<void>((resolve, reject) => {
                        image.onload = () => {
                            setDimensions({
                                width: image.naturalWidth,
                                height: image.naturalHeight,
                            });
                            resolve();
                        };
                        image.onerror = () => {
                            reject(new Error("Image failed to load"));
                        };
                    });
                    const response = await axios.get(url, {
                        responseType: "blob",
                    });
                    setFileSize(response.data.size / 1024);
                }
            } catch (error) {
                console.error("Error fetching file information:", error);
                setFileSize(null);
            }
        };

        fetchFileInformation();
    }, [url, checkVideo]);

    const renderMediaContent = () => {
        if (checkVideo(url)) {
            return (
                <VideoContainer>
                    <ProductVideo
                        source={url}
                        autoPlay={false}
                        muted={false}
                        width="100%"
                        height="500px"
                        borderRadius="8px"
                        stopPlaying={false}
                    />
                </VideoContainer>
            );
        }
        return <VariantImage src={url} alt={formik.values.altText} />;
    };

    const renderDimensionsText = () => {
        if (!dimensions) {
            return "Loading dimensions...";
        }
        if (checkVideo(url)) {
            return `${dimensions.width} x ${dimensions.height} px (estimated)`;
        }
        return `${dimensions.width} x ${dimensions.height} px`;
    };

    const renderFileSizeText = () => {
        if (fileSize === null) {
            return "Loading file size...";
        }
        return `${fileSize.toFixed(2)} KB`;
    };

    const renderUploadDate = () => {
        if (!assetDetails?.createdAt) {
            return "N/A";
        }
        return format(new Date(assetDetails.createdAt), "dd MMM yyyy, hh:mm a");
    };

    const renderUploaderName = () => {
        if (!admin) {
            return "Unknown";
        }
        return `${admin.firstName} ${admin.lastName}`;
    };

    return (
        <StyledDialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
            <StyledDialogTitle>
                <Title>Asset Details</Title>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </StyledDialogTitle>
            <StyledDialogContent>
                {assetDetails && (
                    <>
                        {renderMediaContent()}
                        <DetailsContainer>
                            <DetailRow>
                                <DetailLabel>Hosted Path</DetailLabel>
                                <DetailValue>
                                    <Link
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {url}
                                    </Link>
                                </DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Type</DetailLabel>
                                <DetailValue>{assetDetails.type}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Section</DetailLabel>
                                <Autocomplete
                                    size="small"
                                    options={tagOptions}
                                    getOptionLabel={(option) => option}
                                    value={formik.values.tags || null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            margin="none"
                                        />
                                    )}
                                    disabled={true}
                                />
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Position</DetailLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                    name="position"
                                    value={formik.values.position}
                                    onChange={formik.handleChange}
                                    inputProps={{ min: 1 }}
                                    margin="none"
                                    error={
                                        formik.touched.position &&
                                        Boolean(formik.errors.position)
                                    }
                                    helperText={
                                        formik.touched.position &&
                                        formik.errors.position
                                    }
                                />
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Header</DetailLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    name="header"
                                    value={formik.values.header}
                                    onChange={formik.handleChange}
                                    margin="none"
                                />
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Description</DetailLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    name="description"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    multiline
                                    rows={3}
                                    margin="none"
                                />
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Alt Text</DetailLabel>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    name="altText"
                                    value={formik.values.altText}
                                    onChange={formik.handleChange}
                                    margin="none"
                                />
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Dimensions</DetailLabel>
                                <DetailValue>
                                    {renderDimensionsText()}
                                </DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>File Size</DetailLabel>
                                <DetailValue>
                                    {renderFileSizeText()}
                                </DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Uploaded At</DetailLabel>
                                <DetailValue>{renderUploadDate()}</DetailValue>
                            </DetailRow>
                            <DetailRow>
                                <DetailLabel>Uploaded By</DetailLabel>
                                <DetailValue>
                                    {renderUploaderName()}
                                </DetailValue>
                            </DetailRow>
                        </DetailsContainer>
                    </>
                )}
            </StyledDialogContent>
            <StyledDialogActions>
                <DeleteButton
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={deleteAsset.isLoading}>
                    {deleteAsset.isLoading ? "Deleting..." : "Delete"}
                </DeleteButton>
                <SaveButton
                    variant="contained"
                    color="primary"
                    onClick={formik.submitForm}
                    disabled={!formik.dirty || !formik.isValid}>
                    Save
                </SaveButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};
